import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import ShoppingCart from "@insite/mobius/Icons/ShoppingCart";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomApplicationState from "@sunSource/Store/CustomApplicationState";
import SetOnePageTabData from "@sunSource/Store/Data/NewOnePageCheckout/Handlers/OnePageCheckoutTabHandler";
import {
    getOnePageCheckoutAccountState,
    getOnePageCheckoutTabState,
} from "@sunSource/Store/Data/NewOnePageCheckout/OnePageCheckoutStateSelectors";
import ContactAndCompanyInfo from "@sunSource/Widgets/NewOnePageCheckout/ContactAndCompanyInfo";
import OnePageCustomerValidationAndSetPasswordForm from "@sunSource/Widgets/NewOnePageCheckout/OnePageCustomerValidationAndSetPasswordForm";
import OnePageLoginFormNew from "@sunSource/Widgets/NewOnePageCheckout/OnePageLoginFormNew";
import OnePagePaymentForm from "@sunSource/Widgets/NewOnePageCheckout/OnePagPaymentForm";
import ShippingAddressAndDeliveryMethod from "@sunSource/Widgets/NewOnePageCheckout/ShippingAddressAndDeliveryMethod";
import React, { ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, keyframes } from "styled-components";

const mapStateToProps = (state: CustomApplicationState) => {
        const { cartId } = state.pages.onePageCheckout;
        const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        state,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        accountSettings: getSettingsCollection(state).accountSettings,
        tabState: getOnePageCheckoutTabState(state),
        shouldLoadCountries: !getCurrentCountries(state),
        createAccountData: getOnePageCheckoutAccountState(state),
        isAuthenticated: state?.context?.session?.isAuthenticated,
        cart: getCurrentCartState(state).value,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        isLoading: !cartState.value || cartState.isLoading,
    };
};
const mapDispatchToProps = { signIn, SetOnePageTabData, loadCurrentCountries };

export interface EmptyCartLinesStyles {
    centeringWrapper?: InjectableCss;
    noCartLinesText?: TypographyProps;
    noCartLinesIcon?: IconPresentationProps;
    emptyCart?: InjectableCss;
    leftRow?: GridItemProps;
}

const expand = keyframes`
  from { max-height: 0; opacity: 0; }
  to { max-height: auto; opacity: 1; }
`;

const collapse = keyframes`
  from { max-height: auto; opacity: 1; }
  to { max-height: 0; opacity: 0; }
`;

const AccordionWrapper = styled.div`
    width: 100%;
    border-radius: 0px;
    overflow: hidden;
    font-family: "FrutigerCn";
    border-top: 2px solid #275aa8;
    > div {
        position: relative;
    }
    > div:not(:last-child) {
        border-bottom: 2px solid #d3d3d3 !important;
    }
    > div:not(:last-child)::before {
        content: "";
        display: block;
        width: 2px;
        height: 100%;
        position: absolute;
        left: 23px;
        top: 13px;
        z-index: 1;
        background-color: #d3d3d3;
    }
    input[type="email"],
    input[type="password"],
    input[type="text"],
    textarea,
    select {
        border: none;
        background: #f5f5f8;
        border-radius: 4px;
        border-bottom: 2px solid #d9d8d8;
        height: 40px;
    }
    textarea {
        min-height: 100px;
    }
    select {
        border: none;
        background: #f5f5f8 !important;
        border-radius: 4px !important;
        border-bottom: 2px solid #d9d8d8 !important;
        height: 40px;
    }
`;

type TabButtonProps = {
    active: boolean;
    disabled: boolean;
    isCurrentTab: boolean;
    isCompleted: boolean;
};

const TabButton = styled.button<TabButtonProps>`
    width: 100%;
    display: flex;
    gap: 15px;
    align-items: center;
    padding: 10px;
    background: ${({ isCurrentTab }) => (isCurrentTab ? "#275aa8" : "#f8f8f8")};
    color: ${({ active, isCurrentTab, isCompleted }) =>
        active ? (isCurrentTab ? "white" : "#275aa8") : isCompleted ? "#275aa8" : "#8e8e90"};
    border: none;
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    text-align: left;
    font-size: 16px;
    text-transform: uppercase;
    outline: none;
    &:hover {
        background: ${({ active }) => (active ? "#0056b3" : "#e2e6ea")};
        color: ${({ active, isCompleted }) => (active ? "white" : isCompleted ? "#275aa8" : "#8e8e90")};
    }
`;

type TabContentProps = {
    isOpen: boolean;
};

const TabContent = styled.div<TabContentProps>`
    padding: 10px;
    animation: ${({ isOpen }) => (isOpen ? expand : collapse)} 0.3s ease-in-out forwards;
    overflow: hidden;
    padding-left: 50px;
    padding-right: 35px;
`;
type TabTitleIcon = {
    isCompleted: boolean;
};
const TabTitleIcon = styled.div`
    min-width: 28px;
    max-height: 28px;
    min-height: 28px;
    max-width: 28px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid #d3d3d3;
    display: flex;
    justify-content: center;
    align-items: center;
    background: #f1f1f1;
    position: relative;
    z-index: 2;
`;
const IconWrapper = styled.div`
    padding: 1px;
    background: #275aa8;
    display: flex;
    align-items: center;
    border-radius: 50%;
    justify-content: center;
    width: 20px;
    height: 20px;
`;
const AccordionSectionWrapper = styled.div``;
const PageTitle = styled.h1`
    text-transform: uppercase;
    color: #275aa8;
    line-height: 40px;
    font-size: 24px;
    font-weight: 700;
    font-family: "FrutigerCn";
    margin-bottom: 1rem;
    display: block;
`;

export const emptyCartLinesStyles: EmptyCartLinesStyles = {
    leftRow: {
        width: [12, 12, 12, 12, 12],
    },
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 300px;
            justify-content: center;
            align-items: center;
            margin: 0 auto;
        `,
    },
    noCartLinesText: {
        variant: "h3",
    },
    noCartLinesIcon: {
        src: ShoppingCart,
        size: 45,
        color: "text.link",
        css: css`
            margin-bottom: 20px;
        `,
    },
    emptyCart: {
        css: css`
            & button {
                width: 200px;
            }
        `,
    },
};

type Tab = {
    id: number;
    title: string;
    component: ReactNode;
};

const StyledSection = getStyledWrapper("section");
const styles = emptyCartLinesStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export const OnePageCheckoutAccordion: React.FC<Props> = props => {
    const {
        id,
        accountSettings,
        signInUrl,
        signIn,
        tabState,
        SetOnePageTabData,
        shouldLoadCountries,
        loadCurrentCountries,
        createAccountData,
        isAuthenticated,
        onePageCheckoutPageUrl,
        cart,
        history,
        isLoading
    } = props;

    const [openTabs, setOpenTabs] = useState<number[]>(tabState.openTabs);
    const [completedTabs, setCompletedTabs] = useState<number[]>(tabState.completedTabs);
    const [currentTab, setCurrentTab] = useState<number>(tabState.currentTab);

    const cartLines = props.cart?.cartLines || [];

    const completeAction = (index: number) => {
        setCurrentTab(index + 1);
        if (!completedTabs?.includes(index)) {
            setCompletedTabs([...completedTabs, index]);
            setOpenTabs(prev => [...prev, index + 1]);
        }
    };

    // useEffect(()=>{
    //   SetOnePageTabData({
    //     openTabs,
    //     completedTabs,
    //     currentTab
    //   })
    // },[openTabs,completedTabs,currentTab])
    useEffect(() => {
        setOpenTabs(tabState.openTabs);
        setCompletedTabs(tabState.completedTabs);
        setCurrentTab(tabState.currentTab);
    }, [tabState]);

    React.useEffect(() => {
        if (shouldLoadCountries) {
            loadCurrentCountries();
        }
    }, []);

    const handleClick = (index: number) => {
        if (completedTabs.includes(index) || index === 0) {
            setOpenTabs(prev => (prev.includes(index) ? prev.filter(t => t !== index) : [...prev, index]));
        }
    };

    const tabs: Tab[] = [
        { id: 0, title: "Name & Email", component: <OnePageLoginFormNew /> },
        { id: 1, title: "Contact info & company", component: <ContactAndCompanyInfo /> },
        {
            id: 2,
            title: "Shipping address & delivery method",
            component: (
                <>
                    <ShippingAddressAndDeliveryMethod />
                    <Zone zoneName="Content0" contentId={id} />
                </>
            ),
        },
        {
            id: 3,
            title: "Customer validation & set password",
            component: <OnePageCustomerValidationAndSetPasswordForm />,
        },
        {
            id: 4,
            title: "Payment",
            component: (
                <>
                    <Zone zoneName="Content1" contentId={id} />
                    <OnePagePaymentForm />
                </>
            ),
        },
    ];
    useEffect(() => {
        if(isAuthenticated){
            onePageCheckoutPageUrl && history.push(onePageCheckoutPageUrl);
        } else {
              if(siteMessage("newOnePageCheckout") === "true"){
              }else {
                onePageCheckoutPageUrl && history.push(onePageCheckoutPageUrl);
              }
        }
       }, [isAuthenticated, onePageCheckoutPageUrl]);

    if (cartLines.length === 0) {
        return (
            isLoading ? <StyledWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner data-test-selector="" />
                </StyledWrapper>
                :
            <GridContainer>
                <GridItem {...styles.leftRow}>
                    <StyledSection {...styles.centeringWrapper}>
                        <Icon {...styles.noCartLinesIcon} />
                        <Typography {...styles.noCartLinesText} data-test-selector="cart_noCartLinesMessage">
                            {siteMessage("Cart_NoOrderLines")}
                        </Typography>
                        <StyledWrapper {...styles.emptyCart} className="EmptyCartButton">
                            <Zone contentId={id} zoneName="EmptyCartButton" requireRows />
                        </StyledWrapper>
                    </StyledSection>
                </GridItem>
            </GridContainer>
        );
    }
    return (
        <StyledWrapper style={{ width: "100%" }}>
            <PageTitle>{translate("Check Out")}</PageTitle>
            <AccordionWrapper>
                {tabs.map((tab, index) => {
                    return (
                        <AccordionSectionWrapper key={index}>
                            <TabButton
                                onClick={() => handleClick(index)}
                                disabled={index !== 0 && !completedTabs?.includes(index)}
                                active={openTabs?.includes(index)}
                                isCurrentTab={currentTab === index}
                                isCompleted={!!completedTabs?.includes(index)}
                            >
                                <TabTitleIcon>
                                    {completedTabs?.includes(index) && (
                                        <IconWrapper>
                                            <Icon src={Check} size={16} color="white" />
                                        </IconWrapper>
                                    )}
                                </TabTitleIcon>
                                {tab.title}
                            </TabButton>
                            {openTabs?.includes(index) && (
                                <TabContent isOpen={openTabs?.includes(index)}>
                                    {tab.component}
                                    {/* {index < tabs.length - 1 && (
                    <button onClick={() => completeAction(index)}>Complete & Enable Next</button>
                )} */}
                                </TabContent>
                            )}
                        </AccordionSectionWrapper>
                    );
                })}
            </AccordionWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(OnePageCheckoutAccordion)),
    definition: {
        group: "One Page Checkout",
        displayName: "One Page Checkout Accordion",
    },
};

export default widgetModule;
